import type { ComputedRef, InjectionKey, Ref } from 'vue';
import { computed, inject, unref } from 'vue';
import { AppStore } from '@pkg/sanity-utils/constants/appstore';
import { AnalyticsAppStore } from '@pkg/analytics-helper/src/constants/analyticsAppStore';

/**
 * CMS `config.appstores` fields needed by shared App Store download UI.
 * Hosts provide this via {@link appStoresKey} (web Pinia / business manifest fetch).
 */
export type AppStoresConfig = {
  modalTitleAppStoreDownload?: string | null;
  [AppStore.APPLE_APP_STORE]?: string | null;
  [AppStore.GOOGLE_PLAY_STORE]?: string | null;
  [AppStore.HUAWEI_APP_GALLERY]?: string | null;
  [AppStore.APPLE_APP_STORE_BADGE]?: string | null;
  [AppStore.GOOGLE_PLAY_STORE_BADGE]?: string | null;
  [AppStore.HUAWEI_APP_GALLERY_BADGE]?: string | null;
};

/** Store keys that map to consumer download links / badges in ButtonGroup. */
export type AppStoreDownloadTarget =
  | AppStore.APPLE_APP_STORE
  | AppStore.GOOGLE_PLAY_STORE
  | AppStore.HUAWEI_APP_GALLERY;

/**
 * Use Symbol.for so provide/inject matches across package entry splits
 * (same pattern as site-chrome locationAutocompleteKey).
 */
export const appStoresKey = Symbol.for(
  'tgtg.modular-blocks.appStores',
) as InjectionKey<
  ComputedRef<AppStoresConfig | null | undefined> | Ref<AppStoresConfig | null | undefined>
>;

export type AppStoreOs = `${AnalyticsAppStore}`;

/**
 * Optional Amplitude (or other) download tracking. Web provides; business may omit.
 */
export type AppStoreAnalyticsTracker = {
  trackDownloadApp: (appOs: AppStoreOs) => void;
};

export const appStoreAnalyticsKey = Symbol.for(
  'tgtg.modular-blocks.appStoreAnalytics',
) as InjectionKey<AppStoreAnalyticsTracker>;

/** Reactive CMS app-store config from the host app (null when not provided). */
export function useAppStoresConfig(): ComputedRef<AppStoresConfig | null> {
  const injected = inject(appStoresKey, null);
  return computed(() => (injected ? unref(injected) : null) ?? null);
}
